import { Component } from '@theme/component';

const REVEAL_SELECTOR = [
  '.ml-hero h1',
  '.ml-hero__button',
  '.ml-love h2',
  '.ml-love__card',
  '.ml-categories h2',
  '.ml-categories__card',
  '.ml-story h2',
  '.ml-story__arc',
  '.ml-story__photo',
  '.ml-story .ml-button',
].join(', ');

/** Progressive motion enhancement; content stays visible if JavaScript is unavailable. */
class HomeMotion extends Component {
  #reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  /** @type {AbortController | undefined} */
  #controller;
  /** @type {IntersectionObserver | undefined} */
  #revealObserver;
  /** @type {IntersectionObserver | undefined} */
  #sectionObserver;
  /** @type {WeakSet<Element>} */
  #revealed = new WeakSet();
  /** @type {Map<Element, Animation>} */
  #animations = new Map();
  /** @type {{ element: HTMLElement, anchor: Element, hero: boolean, direction: number }[]} */
  #layers = [];
  /** @type {number | undefined} */
  #scrollFrame;

  connectedCallback() {
    super.connectedCallback();
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    this.#reducedMotion.addEventListener('change', this.#configure, { signal });
    document.addEventListener('shopify:section:load', this.#refresh, { signal });
    document.addEventListener('shopify:section:unload', this.#unload, { signal });
    document.addEventListener('shopify:section:select', this.#showSelection, { signal });
    document.addEventListener('shopify:block:select', this.#showSelection, { signal });
    document.addEventListener('focusin', this.#showSelection, { signal });
    document.addEventListener('visibilitychange', this.#syncVisibility, { signal });
    document.addEventListener('scroll', this.#scheduleScroll, { signal, passive: true });
    window.addEventListener('resize', this.#scheduleScroll, { signal, passive: true });
    window.addEventListener('pageshow', this.#scheduleScroll, { signal });
    document.addEventListener('load', this.#scheduleScroll, { signal, capture: true });
    this.#configure();
    this.#syncVisibility();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
    this.#reset();
    document.body.classList.remove('ml-motion-paused');
  }

  #reset() {
    if (this.#scrollFrame !== undefined) cancelAnimationFrame(this.#scrollFrame);
    this.#scrollFrame = undefined;
    for (const { element } of this.#layers) this.#clearLayer(element);
    this.#layers = [];
    this.#revealObserver?.disconnect();
    this.#sectionObserver?.disconnect();
    this.#revealObserver = undefined;
    this.#sectionObserver = undefined;
    for (const animation of this.#animations.values()) animation.cancel();
    this.#animations.clear();
    for (const section of document.querySelectorAll('.ml-in-view')) section.classList.remove('ml-in-view');
  }

  #configure = () => {
    this.#reset();
    this.dataset.motion = this.#reducedMotion.matches ? 'reduced' : 'enabled';
    this.#revealed = new WeakSet();
    if (this.#reducedMotion.matches) return;

    this.#revealObserver = new IntersectionObserver(this.#reveal, {
      threshold: 0,
      rootMargin: '0px 0px -24px 0px',
    });
    this.#sectionObserver = new IntersectionObserver((entries) => {
      if (this.#reducedMotion.matches || !this.isConnected) return;
      for (const entry of entries) entry.target.classList.toggle('ml-in-view', entry.isIntersecting);
    });
    this.#refresh();
  };

  #syncVisibility = () => {
    document.body.classList.toggle('ml-motion-paused', document.hidden);
    if (!document.hidden) this.#scheduleScroll();
  };

  #refresh = () => {
    if (this.#reducedMotion.matches) return;
    for (const element of document.querySelectorAll(REVEAL_SELECTOR)) {
      if (!this.#revealed.has(element)) this.#revealObserver?.observe(element);
    }
    for (const section of document.querySelectorAll('.ml-section')) this.#sectionObserver?.observe(section);
    const selector = '.ml-hero__scene img, .ml-love__card, .ml-categories__card, .ml-story__photo';
    this.#layers = [...document.querySelectorAll(selector)].flatMap((element, index) => {
      if (!(element instanceof HTMLElement)) return [];
      const hero = element.matches('.ml-hero__scene img');
      const anchor = element.closest('.ml-section');
      return anchor ? [{ element, anchor, hero, direction: index % 2 ? 1 : -1 }] : [];
    });
    this.#scheduleScroll();
  };

  #scheduleScroll = () => {
    if (this.#reducedMotion.matches || !this.isConnected || document.hidden || this.#scrollFrame !== undefined) return;
    this.#scrollFrame = requestAnimationFrame(this.#updateScroll);
  };

  #updateScroll = () => {
    this.#scrollFrame = undefined;
    const viewport = window.innerHeight;
    const intensity = window.innerWidth < 750 ? 0 : 1;
    // Read stable section bounds before writing transforms to their children.
    const positions = this.#layers.map(layer => ({ ...layer, rect: layer.anchor.getBoundingClientRect() }));
    for (const { element, rect, hero, direction } of positions) {
      if (rect.bottom < 0 || rect.top > viewport) continue;
      const progress = Math.max(-1, Math.min(1, (viewport - rect.top * 2 - rect.height) / (viewport + rect.height)));
      const exit = Math.max(0, Math.min(1, -rect.top / rect.height));
      const offset = hero ? exit * Math.min(90, rect.height * 0.1) : progress * direction * 40 * intensity;
      element.style.setProperty('--ml-scroll-y', `${offset.toFixed(2)}px`);
      if (hero) element.style.setProperty('--ml-scroll-scale', `${1.04 + exit * 0.22}`);
      element.classList.add('ml-scroll-layer');
    }
  };

  /** @param {HTMLElement} element */
  #clearLayer(element) {
    element.classList.remove('ml-scroll-layer');
    element.style.removeProperty('--ml-scroll-y');
    element.style.removeProperty('--ml-scroll-scale');
  }

  /** @param {IntersectionObserverEntry[]} entries */
  #reveal = (entries) => {
    if (this.#reducedMotion.matches || !this.isConnected) return;
    for (const entry of entries) {
      if (!entry.isIntersecting) {
        this.#revealed.delete(entry.target);
        continue;
      }
      if (!entry.target.isConnected || this.#revealed.has(entry.target)) continue;
      this.#revealed.add(entry.target);
      if (entry.boundingClientRect.bottom < 0) continue;
      const index = entry.target.parentElement
        ? [...entry.target.parentElement.children].indexOf(entry.target)
        : 0;
      const isPhoto = entry.target.matches('.ml-love__card, .ml-categories__card, .ml-story__photo');
      const compact = window.innerWidth < 750;
      this.#animations.get(entry.target)?.cancel();
      const animation = entry.target.animate([
        { opacity: 0, translate: compact ? '0 16px' : '0 48px', scale: isPhoto ? (compact ? '0.98' : '0.94') : '1' },
        { opacity: 1, translate: '0 0', scale: '1' },
      ], {
        duration: 950,
        delay: Math.min(index, 4) * 90,
        easing: 'cubic-bezier(0.16, 1, 0.3, 1)',
        fill: 'backwards',
      });
      this.#animations.set(entry.target, animation);
      animation.onfinish = () => {
        if (this.#animations.get(entry.target) === animation) this.#animations.delete(entry.target);
      };
    }
  };

  /** @param {Event} event */
  #showSelection = (event) => {
    if (!(event.target instanceof Element)) return;
    const target = event.target;
    this.#layers = this.#layers.filter(({ element }) => {
      if (!element.contains(target) && !target.contains(element)) return true;
      this.#clearLayer(element);
      return false;
    });
    for (const element of document.querySelectorAll(REVEAL_SELECTOR)) {
      if (!element.contains(target) && !target.contains(element)) continue;
      this.#revealObserver?.unobserve(element);
      this.#revealed.add(element);
      this.#animations.get(element)?.cancel();
      this.#animations.delete(element);
    }
  };

  /** @param {Event} event */
  #unload = (event) => {
    if (!(event.target instanceof Element)) return;
    const target = event.target;
    this.#layers = this.#layers.filter(({ element }) => {
      if (!target.contains(element)) return true;
      this.#clearLayer(element);
      return false;
    });
    for (const [element, animation] of this.#animations) {
      if (!target.contains(element)) continue;
      animation.cancel();
      this.#animations.delete(element);
    }
    for (const element of target.querySelectorAll(REVEAL_SELECTOR)) this.#revealObserver?.unobserve(element);
    for (const section of target.querySelectorAll('.ml-section')) this.#sectionObserver?.unobserve(section);
  };
}

if (!customElements.get('ml-home-motion')) customElements.define('ml-home-motion', HomeMotion);
